/**
 * 4.21 Toast: success (a failure uses tone "error": warning icon, alert colour), one at a time, leaves after 5 s, role="status". Use `useToast().show(text)`.
 * D-203: `celebrate(text, { icon, sound })` is the success moment (a badge, a purchase, a free item): the same toast with
 * a burst round its icon and a chime ("chip" and a success for spending, "celebrate" for a badge or a gift).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { CelebrateBurst } from "./Celebrate";
import { chime, purchaseChime } from "../lib/chimes";
import { NETWORK_SENTENCE, SERVER_SENTENCE } from "../lib/api";

interface ToastState { id: number; text: string; icon: string; burst: boolean; tone: "ok" | "error" }
export type CelebrateSound = "purchase" | "celebrate" | "coin" | null;
interface ToastApi {
  show: (text: string, opts?: { icon?: string; tone?: "ok" | "error" }) => void;
  celebrate: (text: string, opts?: { icon?: string; sound?: CelebrateSound }) => void;
  dismiss: () => void;
}
const Ctx = createContext<ToastApi | null>(null);
export const TOAST_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismiss = useCallback(() => { if (timer.current) clearTimeout(timer.current); timer.current = null; setToast(null); }, []);
  const open = useCallback((text: string, icon: string, burst: boolean, tone: "ok" | "error" = "ok") => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ id: Date.now(), text, icon, burst, tone });
    timer.current = setTimeout(() => { timer.current = null; setToast(null); }, TOAST_MS);
  }, []);
  // A failure is never shown with the success check: the app's own connection sentences are errors by default.
  const show = useCallback((text: string, opts?: { icon?: string; tone?: "ok" | "error" }) => {
    const tone = opts?.tone ?? (text === NETWORK_SENTENCE || text.startsWith(SERVER_SENTENCE) ? "error" : "ok");
    open(text, opts?.icon ?? (tone === "error" ? "triangle-alert" : "badge-check"), false, tone);
  }, [open]);
  const celebrate = useCallback((text: string, opts?: { icon?: string; sound?: CelebrateSound }) => {
    const sound = opts?.sound === undefined ? "purchase" : opts.sound;
    if (sound === "purchase") purchaseChime(); else if (sound) chime(sound);
    open(text, opts?.icon ?? "sparkles", true);
  }, [open]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const api = useMemo(() => ({ show, celebrate, dismiss }), [show, celebrate, dismiss]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {typeof document !== "undefined" && createPortal(
        <div className="toast-host" aria-live="polite">
          {toast && (
            <div className={`toast${toast.burst ? " toast--celebrate" : ""}${toast.tone === "error" ? " toast--error" : ""}`} role="status" key={toast.id}>
              <span className="toast__icon">{toast.burst ? <CelebrateBurst icon={toast.icon} size={22} /> : <Icon name={toast.icon} size={22} />}</span>
              <span className="toast__text">{toast.text}</span>
            </div>
          )}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}
export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast must be used inside ToastProvider");
  return v;
}
